import { useToast } from '@components/index'
import { RentPaidFields } from '@features/contracts/components/RentPaidFields'
import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'

interface BulkMarkPaidModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  rentIds: string[]
  /**
   * Called after a run with the ids that failed — empty when everything went
   * through. The parent narrows its selection to these so a retry only re-sends
   * what didn't land.
   */
  onSettled: (failedIds: string[]) => void
}

const today = () => dayjs().format('YYYY-MM-DD')

/**
 * Collects payment date, note and paid flag once, then applies them to every
 * selected rent. Replaces the old one-click bulk write that hardcoded today's
 * date — landlord payments are sent as one bank batch, but rarely ticked off on
 * the day they were actually paid.
 */
export function BulkMarkPaidModal({
  open,
  onOpenChange,
  rentIds,
  onSettled
}: BulkMarkPaidModalProps) {
  const { t } = useTranslation('dashboard')
  const toast = useToast()
  const queryClient = useQueryClient()

  const [paid, setPaid] = useState(true)
  const [paidDate, setPaidDate] = useState<string | undefined>(today)
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Restore the defaults on every open. The modal stays mounted, so without
  // this it would reopen holding the previous run's values — and `paidDate`
  // would keep the date computed when the page was first loaded.
  useEffect(() => {
    if (!open) return
    setPaid(true)
    setPaidDate(today())
    setDescription('')
  }, [open])

  const handleSubmit = async () => {
    if (!rentIds.length) return

    // An untouched note is left out of the payload entirely, so the existing
    // note on each payment survives. A filled-in one overwrites, like the
    // single-rent edit modal does.
    const body = {
      paid,
      paid_date: paidDate ?? null,
      ...(description.trim() ? { description } : {})
    }

    setSubmitting(true)
    try {
      const results = await Promise.allSettled(
        rentIds.map((id) => apiClient.patch(`${SNA_PREFIX}rents/${id}/`, body))
      )
      const failedIds = rentIds.filter(
        (_, i) => results[i]?.status === 'rejected'
      )
      const done = rentIds.length - failedIds.length

      if (done > 0) {
        queryClient.invalidateQueries({ queryKey: ['dashboard-unpaid-rents'] })
        queryClient.invalidateQueries({ queryKey: ['rents'] })
      }

      onSettled(failedIds)

      if (!failedIds.length) {
        toast.success({ message: t('rentTable.markedPaid') })
        onOpenChange(false)
      } else if (done > 0) {
        // Stay open: the selection now holds only the failures, so hitting
        // confirm again retries exactly those.
        toast.warning({
          message: t('rentTable.markedPaidPartial', {
            done,
            total: rentIds.length
          })
        })
      } else {
        toast.error({ message: t('rentTable.markedPaidError') })
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Check className="h-5 w-5" />
            </span>
            <div>
              <DialogTitle>{t('rentTable.bulkPaid.title')}</DialogTitle>
              <DialogDescription>
                {t('rentTable.bulkPaid.subtitle', { count: rentIds.length })}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <RentPaidFields
          paid={paid}
          onPaidChange={setPaid}
          paidDate={paidDate}
          onPaidDateChange={setPaidDate}
          description={description}
          onDescriptionChange={setDescription}
          descriptionPlaceholder={t('rentTable.bulkPaid.notePlaceholder')}
          clearDateWhenUnpaid
        />

        <DialogFooter>
          <Button variant="ghostDark" onClick={() => onOpenChange(false)}>
            {t('rentTable.bulkPaid.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !rentIds.length}
          >
            {t(
              paid
                ? 'rentTable.bulkPaid.confirmPaid'
                : 'rentTable.bulkPaid.confirmUnpaid',
              { count: rentIds.length }
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
